import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { Rnd } from 'react-rnd';
import { useDispatch, useSelector } from 'react-redux';
import {
  createEditor,
  BaseEditor,
  Descendant,
  Editor,
  Range,
  Transforms,
  Element as SlateElement,
  Point,
} from 'slate';
import {
  Slate,
  Editable,
  withReact,
  ReactEditor,
  RenderElementProps,
  RenderLeafProps,
  useSelected,
  useFocused,
  useSlateStatic,
} from 'slate-react';
import { RootState } from '../../../redux';
import { updateRichTextElement, setSelectedRichTextId } from '../../../redux/TabTile/TabActions';
import { RichTextElement } from '../../../redux/TabTile/TabStateInterfaces';
import { CustomEditor, CustomText, LinkElement } from './SlateEditor';
import { AcceptRejectDialog } from '../../CommonFunctions/DialogComponents';
import { acceptBtnStyle, rejectBtnStyle } from '../ButtonDialogStyles';

interface DraggableRichTextProps {
  id: string;
  element: RichTextElement;
  onDragStop: (id: string, x: number, y: number) => void;
  onResizeStop: (id: string, width: number, height: number, x: number, y: number) => void;
  onDelete: (id: string) => void;
  onClick: (id: string) => void;
  softUI: boolean;
  colorScheme?: string;
  color?: string;
  backgroundColor?: string;
  dashMode: string;
  isSelected: boolean;
  setActiveEditor: (editor: Editor | null) => void;
  onEditorChange?: () => void;
  dashboardHeight?: number;
  dashboardWidth?: number;
}

const Leaf: React.FC<RenderLeafProps> = ({ attributes, children, leaf }) => {
  const editor = useSlateStatic();
  let el: React.ReactNode = children;

  if (leaf.bold) el = <strong>{el}</strong>;
  if (leaf.italic) el = <em>{el}</em>;
  if (leaf.underline) el = <u>{el}</u>;
  if (leaf.strikethrough) el = <span style={{ textDecoration: 'line-through' }}>{el}</span>;
  if (leaf.superscript) {
    el = <sup style={{ verticalAlign: 'super', fontSize: 'smaller' }}>{el}</sup>;
  } else if (leaf.subscript) {
    el = <sub style={{ verticalAlign: 'sub', fontSize: 'smaller' }}>{el}</sub>;
  }

  // Applying color, fontSize, fontFamily on the outermost leaf span so that
  // CSS-applied underlines (e.g. inside links) inherit the text color.
  const leafStyle: React.CSSProperties = {};
  if (leaf.color) leafStyle.color = leaf.color;
  if (leaf.fontSize) leafStyle.fontSize = `${leaf.fontSize}px`;
  if (leaf.fontFamily) leafStyle.fontFamily = leaf.fontFamily;

  return (
    <span {...attributes} style={leafStyle}>
      {el}
    </span>
  );
};

const LinkElementComponent: React.FC<RenderElementProps> = ({ attributes, children, element }) => {
  const selected = useSelected();
  const focused = useFocused();
  const linkEl = element as unknown as LinkElement;
  const linkRef = useRef<HTMLSpanElement>(null);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});
  const [tooltipMaxWidth, setTooltipMaxWidth] = useState<number>(100);

  // this useEffect is for tooltip dynamic position and tooltip max width
  useEffect(() => {
    if (selected && focused && linkRef.current) {
      const linkRect = linkRef.current.getBoundingClientRect();

      const container = linkRef.current.closest('.custom-slate-editor');
      if (!container) return;
      const containerRect = container.getBoundingClientRect();

      const spaceLeft = linkRect.left - containerRect.left;
      const spaceRight = containerRect.right - linkRect.left;

      const tooltipPadding = 6; //  padding on each side

      // Picking the side with more space, or center if enough room
      if (spaceRight >= spaceLeft) {
        // Show aligned to the left edge of the link
        setTooltipStyle({ left: 0, right: 'auto' });
        setTooltipMaxWidth(Math.max(80, spaceRight - tooltipPadding));
      } else {
        // Show aligned to the right edge of the link
        setTooltipStyle({ right: 0, left: 'auto' });
        setTooltipMaxWidth(Math.max(80, spaceLeft - tooltipPadding));
      }
    }
  }, [selected, focused]);

  return (
    <span ref={linkRef} style={{ position: 'relative', display: 'inline' }}>
      <a
        {...attributes}
        href={linkEl.href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => {
          e.preventDefault();
          window.open(linkEl.href, '_blank', 'noopener,noreferrer');
        }}
        className="slate-link"
        style={{
          color: '#2BB9BB',
          textDecoration: 'none',
          cursor: 'pointer',
        }}
      >
        {children}
      </a>
      {selected && focused && (
        <span
          contentEditable={false}
          style={{
            position: 'absolute',
            top: '100%',
            marginTop: 6,
            display: 'flex',
            alignItems: 'center',
            backgroundColor: 'white',
            padding: '4px 6px',
            gap: 8,
            borderRadius: 6,
            border: '1px solid lightgray',
            zIndex: 10,
            fontSize: 12,
            maxWidth: tooltipMaxWidth,
            boxSizing: 'border-box',
            ...tooltipStyle,
          }}
        >
          <a
            href={linkEl.href}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: '#2BB9BB',
              textDecoration: 'underline',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
            }}
          >
            {linkEl.href}
          </a>
        </span>
      )}
    </span>
  );
};

const Element: React.FC<RenderElementProps> = (props) => {
  const { attributes, children, element } = props;
  const style: React.CSSProperties = {
    margin: 0,
    ...('align' in element ? { textAlign: element.align } : {}),
  };

  switch (element.type) {
    case 'link':
      return <LinkElementComponent {...props} />;
    case 'heading-one':
      return (
        <h1 style={style} {...attributes}>
          {children}
        </h1>
      );
    case 'heading-two':
      return (
        <h2 style={style} {...attributes}>
          {children}
        </h2>
      );
    case 'heading-three':
      return (
        <h3 style={style} {...attributes}>
          {children}
        </h3>
      );
    case 'blockquote':
      return (
        <blockquote style={style} {...attributes}>
          {children}
        </blockquote>
      );
    case 'list-item':
      return (
        <li style={style} {...attributes}>
          {children}
        </li>
      );
    case 'ordered-list':
      return (
        <ol style={{ ...style, listStylePosition: 'inside' }} {...attributes}>
          {children}
        </ol>
      );
    case 'unordered-list':
      return (
        <ul style={{ ...style, listStylePosition: 'inside' }} {...attributes}>
          {children}
        </ul>
      );
    case 'paragraph':
    default:
      return (
        <p style={style} {...attributes}>
          {children}
        </p>
      );
  }
};


const DraggableRichText: React.FC<DraggableRichTextProps> = ({
  id,
  element,
  onDragStop,
  onResizeStop,
  onDelete,
  onClick,
  softUI,
  colorScheme = 'peacock',
  dashMode,
  color,
  dashboardHeight,
  dashboardWidth,
  isSelected,
  backgroundColor,
  setActiveEditor,
  onEditorChange,
}) => {
  const dispatch = useDispatch();
  const rndRef = useRef<any>(null);
  const prevBgColorRef = useRef<string | undefined>();
  const tabTileProps = useSelector((state: RootState) => state.tabTileProps);
  const { dashGridSize } = tabTileProps;
  const gridSizeX = dashGridSize.x;
  const gridSizeY = dashGridSize.y;

  const [isHovered, setIsHovered] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const isCurrentlyEditing = dashMode === 'Edit' && isSelected;

  // get active link
  const getActiveLink = (editor: Editor) => {
    return Editor.above(editor, {
      match: (n) => SlateElement.isElement(n) && n.type === 'link',
    });
  };
  const withLinks = (editor: BaseEditor & ReactEditor) => {
    const { isInline, insertText, insertBreak } = editor;
    editor.isInline = (element: any) => (element.type === 'link' ? true : isInline(element));

    editor.insertText = (text) => {
      const { selection } = editor;

      if (selection && Range.isCollapsed(selection)) {
        const linkEntry = getActiveLink(editor);

        if (linkEntry) {
          const [, linkPath] = linkEntry;
          const start = Editor.start(editor, linkPath);
          const end = Editor.end(editor, linkPath);

          if (Point.equals(selection.anchor, end)) {
            // Typing at the very end — move out after link
            const afterPoint = Editor.after(editor, linkPath);
            if (afterPoint) {
              Transforms.select(editor, afterPoint);
            } else {
              const parentPath = linkPath.slice(0, -1);
              const nextIndex = linkPath[linkPath.length - 1] + 1;
              Transforms.insertNodes(editor, { text: '' } as any, {
                at: [...parentPath, nextIndex],
              });
              Transforms.select(editor, [...parentPath, nextIndex]);
            }
          } else if (Point.equals(selection.anchor, start)) {
            // Typing at the very start — move out before link
            const beforePoint = Editor.before(editor, linkPath);
            if (beforePoint) {
              Transforms.select(editor, beforePoint);
            } else {
              const parentPath = linkPath.slice(0, -1);
              const index = linkPath[linkPath.length - 1];
              Transforms.insertNodes(editor, { text: '' } as any, {
                at: [...parentPath, index],
              });
              Transforms.select(editor, [...parentPath, index]);
            }
          }
        }
      }

      insertText(text);
    };

    // this is added so we dont see tooltip when we press enter key at the end of the link
    editor.insertBreak = () => {
      const { selection } = editor;

      if (selection && Range.isCollapsed(selection)) {
        const linkEntry = getActiveLink(editor);

        if (linkEntry) {
          const [, linkPath] = linkEntry;
          const start = Editor.start(editor, linkPath);
          const end = Editor.end(editor, linkPath);

          if (Point.equals(selection.anchor, end)) {
            const afterPoint = Editor.after(editor, linkPath);
            if (afterPoint) {
              Transforms.select(editor, afterPoint);
            } else {
              const parentPath = linkPath.slice(0, -1);
              const nextIndex = linkPath[linkPath.length - 1] + 1;
              Transforms.insertNodes(editor, { text: '' } as any, {
                at: [...parentPath, nextIndex],
              });
              Transforms.select(editor, [...parentPath, nextIndex]);
            }
          } else if (Point.equals(selection.anchor, start)) {
            const beforePoint = Editor.before(editor, linkPath);
            if (beforePoint) {
              Transforms.select(editor, beforePoint);
            } else {
              const parentPath = linkPath.slice(0, -1);
              const index = linkPath[linkPath.length - 1];
              Transforms.insertNodes(editor, { text: '' } as any, {
                at: [...parentPath, index],
              });
              Transforms.select(editor, [...parentPath, index]);
            }
          }
        }
      }

      insertBreak();
    };

    return editor;
  };

  const editor = useMemo(
    () => withLinks(withReact(createEditor() as BaseEditor & ReactEditor)),
    [],
  );

  useEffect(() => {
    if (
      backgroundColor &&
      backgroundColor !== element.backgroundColor &&
      !element.isBackgroundManual
    ) {
      dispatch(
        updateRichTextElement(tabTileProps.selectedTabId, id, {
          backgroundColor,
        }),
      );
    }
  }, [backgroundColor]);

  useEffect(() => {
    if (isSelected) {
      setActiveEditor(editor);
    }
  }, [isSelected, editor, setActiveEditor]);

  useEffect(() => {
    CustomEditor.clearStickyMarks(editor);
  }, [editor]);

  useEffect(() => {
    if (isCurrentlyEditing) {
      ReactEditor.focus(editor);
      Transforms.select(editor, Editor.end(editor, []));
    }
  }, [isCurrentlyEditing, editor]);

  useEffect(() => {
    const { selection } = editor;
    if (!selection || !Range.isCollapsed(selection)) return;

    const linkEntry = getActiveLink(editor);
    const leafColor = CustomEditor.getLeafColor(editor);

    if (linkEntry) {
      const [, linkPath] = linkEntry;
      const start = Editor.start(editor, linkPath);

      // If selection is exactly at the start of the link, we treat it as being outside
      // to prevent the selection from snapping into the link when marks are added.
      const isAtStart = Point.equals(selection.anchor, start);

      if (!leafColor && !isAtStart) {
        Editor.addMark(editor, 'color', '#2BB9BB');
      }
    } else if (!leafColor) {
      // Cursor is on text with no color mark — clearing stale color from sticky marks
      CustomEditor.clearStickyColor(editor);
    }
  }, [editor, editor.selection, color]);

  const initialValue: Descendant[] =
    element.value && element.value.length
      ? element.value
      : [{ type: 'paragraph', children: [{ text: 'Enter your text here...', fontSize: 12 }] }];

  const constrainPosition = useCallback(
    (currentX: number, currentY: number, currentWidth: number, currentHeight: number) => {
      const effectiveHeight = dashboardHeight || document.body.getBoundingClientRect().height;
      const effectiveWidth = dashboardWidth || document.body.getBoundingClientRect().width;

      const newWidth = currentWidth * gridSizeX;
      const newHeight = currentHeight * gridSizeY;

      const maxX = Math.max(0, (effectiveWidth - newWidth) / gridSizeX);
      const maxY = Math.max(0, (effectiveHeight - newHeight) / gridSizeY);

      const newX = Math.min(Math.max(0, currentX), maxX);
      const newY = Math.min(Math.max(0, currentY), maxY);

      return { x: newX, y: newY };
    },
    [dashboardHeight, dashboardWidth, gridSizeX, gridSizeY],
  );

  const handleDragStop = (e: any, d: any) => {
    const constrainedPos = constrainPosition(
      d.x / gridSizeX,
      d.y / gridSizeY,
      element.width,
      element.height,
    );
    onDragStop(id, constrainedPos.x, constrainedPos.y);
  };

  const handleResizeStop = (e: any, direction: any, ref: any, delta: any, newPos: any) => {
    const newWidth = parseInt(ref.style.width, 10) / gridSizeX;
    const newHeight = parseInt(ref.style.height, 10) / gridSizeY;

    const constrainedPos = constrainPosition(
      newPos.x / gridSizeX,
      newPos.y / gridSizeY,
      newWidth,
      newHeight,
    );

    onResizeStop(id, newWidth, newHeight, constrainedPos.x, constrainedPos.y);
  };

  const handleTextChange = (value: Descendant[]) => {
    if (editor.operations.some((op) => op.type !== 'set_selection')) {
      dispatch(updateRichTextElement(tabTileProps.selectedTabId, id, { value }));
    }
    onEditorChange?.();
  };
  const handleClick = () => {
    onClick(id);
    dispatch(setSelectedRichTextId(tabTileProps.selectedTabId, id));
    setActiveEditor(editor);
  };

  const renderLeaf = useCallback((props: RenderLeafProps) => <Leaf {...props} />, []);
  const renderElement = useCallback((props: RenderElementProps) => <Element {...props} />, []);

  const getBoxShadow = () => {
    if (!softUI) return 'none';
    switch (colorScheme) {
      case 'dark':
        return '0 0 20px rgba(0,0,0,0.6)';
      case 'chalk':
        return '0 0 18px rgba(0,0,0,0.4)';
      case 'halloween':
        return '0 0 15px rgba(0,0,0,0.3)';
      case 'purplePassion':
        return '0 0 20px rgba(0,0,0,0.5)';
      default:
        return '0 0 15px rgba(0,0,0,0.12)';
    }
  };

  const containerStyle: React.CSSProperties = {
    backgroundColor: element.backgroundColor || '#ffffff',
    borderRadius: element.borderRadius ? `${element.borderRadius}px` : softUI ? '12px' : '0px',
    boxShadow: getBoxShadow(),
    overflow: 'hidden',
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    border: softUI ? 'none' : '1px solid #e0e0e0',
    transition: 'all 0.2s ease',
    position: 'relative',
    padding: element.padding ? `${element.padding}px` : '0px',
    boxSizing: 'border-box',
    cursor: dashMode === 'Edit' ? (isCurrentlyEditing ? 'text' : 'move') : 'default',
  };

  const editableStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    outline: 'none',
    overflowY: 'auto',
    overflowX: 'hidden',
    color: color,
    cursor: isCurrentlyEditing ? 'text' : 'inherit',
  };

  return (
    <Rnd
      ref={rndRef}
      size={{ width: element.width * gridSizeX, height: element.height * gridSizeY }}
      position={{ x: element.x * gridSizeX, y: element.y * gridSizeY }}
      onDragStop={handleDragStop}
      onResizeStop={handleResizeStop}
      bounds="parent"
      enableResizing={dashMode === 'Edit'}
      minWidth={100}
      minHeight={40}
      dragGrid={[gridSizeX, gridSizeY]}
      resizeGrid={[gridSizeX, gridSizeY]}
      className="dashboard-richtext"
      onClick={handleClick}
      disableDragging={dashMode !== 'Edit' || isCurrentlyEditing}
      style={{ zIndex: 2 }}
    >
      <div
        style={containerStyle}
        onMouseEnter={() => dashMode === 'Edit' && setIsHovered(true)}
        onMouseLeave={() => dashMode === 'Edit' && setIsHovered(false)}
      >
        <Slate editor={editor} initialValue={initialValue} onChange={handleTextChange}>
          <Editable
            readOnly={!isCurrentlyEditing}
            renderElement={renderElement}
            renderLeaf={renderLeaf}
            style={editableStyle}
            className="custom-slate-editor"
            placeholder="Enter text..."
            onFocus={() => {
              CustomEditor.applyStickyMarks(editor);
            }}
          />
        </Slate>

        {isHovered && dashMode === 'Edit' && (
          <div className="DraggableImage-delete-button-container">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setConfirmOpen(true);
              }}
            >
              ×
            </button>
          </div>
        )}
        <AcceptRejectDialog
          open={confirmOpen}
          heading={`Remove Rich Text`}
          messages={[
            { text: `Are you sure you want to remove this Rich Text from the dashboard?` },
          ]}
          acceptText="Remove"
          rejectText="Cancel"
          acceptButtonStyle={acceptBtnStyle}
          rejectButtonStyle={rejectBtnStyle}
          acceptFunction={() => {
            onDelete(id);
            setConfirmOpen(false);
          }}
          rejectFunction={() => setConfirmOpen(false)}
          closeFunction={() => setConfirmOpen(false)}
        />
      </div>
    </Rnd>
  );
};

export default DraggableRichText;
